import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bot, CheckCircle2, XCircle, Search, Filter, RotateCcw, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AIAgentActivityPage — Super-admin-only audit of every AI tool execution.
 * Shows who ran what, when, with what result, and whether it succeeded.
 * Also surfaces the AI access grant list.
 */
const AIAgentActivityPage = () => {
  const [tab, setTab] = useState('activity'); // 'activity' | 'access'
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyFailures, setOnlyFailures] = useState(false);
  const [search, setSearch] = useState('');

  const loadActivity = useCallback(async () => {
    setLoading(true);
    try {
      const params = onlyFailures ? { only_failures: true, limit: 200 } : { limit: 200 };
      const { data } = await axios.get(`${API}/admin/ai-agent/activity`, {
        params, withCredentials: true,
      });
      setRows(data.rows || []);
    } catch {
      toast.error('Could not load AI activity');
    } finally {
      setLoading(false);
    }
  }, [onlyFailures]);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ai-agent/users`, { withCredentials: true });
      setUsers(data.users || []);
    } catch {
      toast.error('Could not load users');
    }
  }, []);

  useEffect(() => { loadActivity(); loadUsers(); }, [loadActivity, loadUsers]);

  const toggleAccess = async (u) => {
    const nextVal = !u.ai_agent_access;
    try {
      await axios.patch(
        `${API}/admin/users/${u.id}/ai-agent-access`,
        { enabled: nextVal },
        { withCredentials: true },
      );
      setUsers((prev) =>
        prev.map((p) => (p.id === u.id ? { ...p, ai_agent_access: nextVal } : p)),
      );
      toast.success(`AI Agent ${nextVal ? 'enabled' : 'disabled'} for ${u.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update AI access');
    }
  };

  const filteredRows = rows.filter((r) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (r.user_name || '').toLowerCase().includes(s) ||
      (r.user_email || '').toLowerCase().includes(s) ||
      (r.tool_id || '').toLowerCase().includes(s)
    );
  });

  const filteredUsers = users.filter((u) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(s) ||
      (u.email || '').toLowerCase().includes(s) ||
      (u.role || '').toLowerCase().includes(s)
    );
  });

  return (
    <div className="p-4 sm:p-6" data-testid="ai-agent-activity-page">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#002249] text-white flex items-center justify-center">
          <Bot className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">AI Agent — Oversight</h1>
          <p className="text-sm text-slate-500 mt-1">
            Super-admin view of AI tool executions and per-user access control.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-4">
        {[
          { k: 'activity', label: 'Activity log' },
          { k: 'access',   label: 'Access control' },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`relative px-4 py-2.5 text-[13px] font-semibold transition-colors ${
              tab === t.k ? 'text-[#002249]' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`ai-oversight-tab-${t.k}`}
          >
            {t.label}
            {tab === t.k && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#002249]" />}
          </button>
        ))}
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === 'activity' ? 'Filter by user, email or tool…' : 'Filter users…'}
            className="pl-9"
            data-testid="ai-oversight-search"
          />
        </div>
        {tab === 'activity' && (
          <>
            <Button
              variant={onlyFailures ? 'default' : 'outline'}
              size="sm"
              onClick={() => setOnlyFailures((v) => !v)}
              className={onlyFailures ? 'bg-rose-600 hover:bg-rose-700' : ''}
              data-testid="ai-oversight-failures-toggle"
            >
              <Filter className="w-3.5 h-3.5 mr-1.5" />
              {onlyFailures ? 'Showing failures' : 'Only failures'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadActivity}
              disabled={loading}
              data-testid="ai-oversight-refresh"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            </Button>
          </>
        )}
      </div>

      {/* Activity log */}
      {tab === 'activity' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">When</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">User</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Tool</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Risk</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Result</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-slate-400">
                      No activity yet.
                    </td>
                  </tr>
                )}
                {filteredRows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 align-top" data-testid={`ai-audit-row-${r.id}`}>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-[#1a1a1a]">{r.user_name || '—'}</div>
                      <div className="text-[11px] text-slate-500">{r.user_email}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11.5px] text-[#002249]">{r.tool_id}</td>
                    <td className="px-3 py-2">
                      {r.risk === 'high_impact' ? (
                        <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">HIGH</span>
                      ) : (
                        <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">low</span>
                      )}
                    </td>
                    <td className="px-3 py-2 max-w-md">
                      {r.ok ? (
                        <div className="flex items-start gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className="text-[11.5px] text-slate-700 line-clamp-2">{r.result_summary}</span>
                        </div>
                      ) : (
                        <div className="flex items-start gap-1.5">
                          <XCircle className="w-3.5 h-3.5 text-rose-500 flex-shrink-0 mt-0.5" />
                          <span className="text-[11.5px] text-rose-700">{r.error || 'Failed'}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Access control */}
      {tab === 'access' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">User</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Role</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Leadership</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">AI access</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Toggle</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ai-access-row-${u.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-[#1a1a1a]">{u.name}</div>
                      <div className="text-[11px] text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{u.role?.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-slate-600">{u.leadership_level || '—'}</td>
                    <td className="px-3 py-2">
                      {u.role === 'super_admin' ? (
                        <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">ALWAYS (super admin)</span>
                      ) : u.ai_agent_access ? (
                        <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">ENABLED</span>
                      ) : (
                        <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">off</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {u.role === 'super_admin' ? (
                        <span className="text-[11px] text-slate-400">—</span>
                      ) : (
                        <button
                          onClick={() => toggleAccess(u)}
                          className={`px-3 py-1.5 rounded-md text-[12px] font-semibold ${
                            u.ai_agent_access
                              ? 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                              : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                          }`}
                          data-testid={`ai-access-toggle-${u.id}`}
                        >
                          {u.ai_agent_access ? 'Revoke' : 'Enable'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAgentActivityPage;
